import { useMemo, useRef, type ReactNode } from 'react';
import { useCapabilities } from '@lemontaigu/platform-ui';
import {
  SupabaseBoardRealtime,
  SupabaseBoardStore,
  SupabaseBoardWriteStore,
  SupabaseChannelReadStore,
  SupabaseChannelRealtime,
  SupabaseFeedProvenanceStore,
  SupabaseMetricStore,
  SupabaseRosterStore,
  SupabaseShiftRequestStore,
  SupabaseStaffingConfigStore,
  SupabaseTimesheetStore,
} from '@lemontaigu/staff/adapters/client';
import {
  createBoardDemandService,
  createBoardPresenceService,
  createMetricRegistryService,
  createShiftRequestService,
  createStaffingConfigService,
} from '@lemontaigu/staff';
import { StaffStoresProvider } from '@lemontaigu/staff/react';
import { supabase } from './supabase.js';

/**
 * The C3 composition root for the staff app (ladder step 6) — the `PmsProvider`
 * shape that ARCHITECTURE.md §2 names as the reference for a React SPA.
 *
 * The ONE place in this app that binds the concrete Supabase client to C3's
 * read ports. Views call `useBoard()` / `useRoster()` / `useTimesheet()` and
 * depend only on the interfaces.
 *
 * Same three stores as the reception app's root, over the SAME adapters but a
 * DIFFERENT authed client — this app's session is an individual employee's, so
 * RLS returns that employee's slice. Duplicating the wiring per app is the
 * point of a composition root, not a smell: one runtime, one root.
 */
export function StaffProvider({ children }: { children: ReactNode }) {
  // The L2 gate's actor source (F3 increment 3), through a ref so it stays live
  // across the async capability fetch. Only `boardDemand` uses it here: presence
  // is identity-reachable and deliberately ungated (ladder §3.19), which is the
  // whole reason an employee can still tap themselves onto an activity.
  const { actor } = useCapabilities();
  const actorRef = useRef(actor);
  actorRef.current = actor;

  const stores = useMemo(() => {
    const boardWrites = new SupabaseBoardWriteStore(supabase);
    const actorSrc = { current: () => actorRef.current };
    return {
      board: new SupabaseBoardStore(supabase),
      roster: new SupabaseRosterStore(supabase),
      timesheet: new SupabaseTimesheetStore(supabase),
      channel: new SupabaseChannelReadStore(supabase),
      boardPresence: createBoardPresenceService(boardWrites),
      boardDemand: createBoardDemandService(boardWrites, actorSrc),
      boardRealtime: new SupabaseBoardRealtime(supabase),
      // The « importé à … » stamp on the staff Charge board (2026-09-05).
      // `board_feed_provenance()` is gated on `rh.board.read`, which an
      // employee holds — whoever may read the figures may read their
      // freshness. Without it the stamp degrades to « import inconnu », and a
      // stopped feed would look exactly like a running one on the ONE surface
      // where nobody can type a correction anyway (RUNBOOK §20.7).
      feedProvenance: new SupabaseFeedProvenanceStore(supabase),
      channelRealtime: new SupabaseChannelRealtime(supabase),
      // Read-only in this runtime: an employee holds `rh.board.read` (so the
      // badge on their own Gantt resolves) but never `rh.board.manage`, which
      // every write on this service is gated on (ADR-0015).
      metricRegistry: createMetricRegistryService(new SupabaseMetricStore(supabase), actorSrc),
      // Read-only too, and for the badge specifically: « à blanc équivalent » is
      // published BY a staffing profile, so without the profile's resolved
      // outputs that metric cannot evaluate and the badge would silently fall
      // back to the raw demand number — the same activity showing two different
      // figures on two screens, which is the defect ADR-0015 exists to remove.
      staffingConfig: createStaffingConfigService(new SupabaseStaffingConfigStore(supabase), actorSrc),
      // « Corriger » / « J'ai oublié de pointer » (Mes pointages). Identity-reachable:
      // the RPC reads the caller's employee link from the JWT, so there is no
      // gate to bind and no employee id for the client to forge.
      shiftRequests: createShiftRequestService(new SupabaseShiftRequestStore(supabase)),
    };
  }, []);
  return <StaffStoresProvider stores={stores}>{children}</StaffStoresProvider>;
}
